import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const STORAGE_KEY = 'whot_help_btn_pos';
const BUTTON_SIZE = 52;
const NAV_HEIGHT = 80;
const EDGE_GAP = 10;
const DRAG_THRESHOLD = 6;

function defaultPosition() {
  return {
    left: Math.max(EDGE_GAP, window.innerWidth - BUTTON_SIZE - EDGE_GAP),
    top: Math.max(8, Math.min(88, window.innerHeight - NAV_HEIGHT - BUTTON_SIZE - EDGE_GAP)),
  };
}

function clampPosition(position) {
  const maxLeft = Math.max(EDGE_GAP, window.innerWidth - BUTTON_SIZE - EDGE_GAP);
  const maxTop = Math.max(8, window.innerHeight - BUTTON_SIZE - EDGE_GAP);
  return {
    left: Math.min(Math.max(EDGE_GAP, Number(position?.left) || 0), maxLeft),
    top: Math.min(Math.max(8, Number(position?.top) || 0), maxTop),
  };
}

function readSavedPosition() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const left = Number(parsed?.left);
    const top = Number(parsed?.top);
    const maxLeft = Math.max(EDGE_GAP, window.innerWidth - BUTTON_SIZE - EDGE_GAP);
    const maxTop = Math.max(8, window.innerHeight - NAV_HEIGHT - BUTTON_SIZE - EDGE_GAP);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    if (left < EDGE_GAP || left > maxLeft || top < 8 || top > maxTop) return null;
    return { left, top };
  } catch {
    return null;
  }
}

function savePosition(position) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Storage may be unavailable in private/restricted browser contexts.
  }
}

export default function SupportLauncher() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const pendingPositionRef = useRef(null);
  const movedRef = useRef(false);

  const isWhotGame = location.pathname.startsWith('/play/whot/');
  const shouldRender = isAuthenticated && !location.pathname.startsWith('/support') && !location.pathname.startsWith('/admin');

  const applyPosition = useCallback((position) => {
    const next = clampPosition(position);
    pendingPositionRef.current = next;
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      const target = buttonRef.current;
      const pending = pendingPositionRef.current;
      if (!target || !pending) return;
      target.style.left = String(pending.left) + 'px';
      target.style.top = String(pending.top) + 'px';
    });
  }, []);

  useEffect(() => {
    if (!isWhotGame) return undefined;
    const target = buttonRef.current;
    if (!target) return undefined;

    const saved = readSavedPosition();
    // Older saved positions commonly put the launcher over the bottom hand.
    // Migrate those lower-half positions to the upper-right safe area once.
    const staleHandPosition = saved && saved.top > window.innerHeight * 0.5;
    const position = staleHandPosition ? defaultPosition() : (saved || defaultPosition());
    if (!saved || staleHandPosition) savePosition(position);
    target.style.left = String(position.left) + 'px';
    target.style.top = String(position.top) + 'px';

    const handleResize = () => {
      const current = {
        left: parseFloat(target.style.left),
        top: parseFloat(target.style.top),
      };
      const next = clampPosition(current);
      target.style.left = String(next.left) + 'px';
      target.style.top = String(next.top) + 'px';
      savePosition(next);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [isWhotGame]);

  const handlePointerDown = (event) => {
    if (!isWhotGame || (event.button !== undefined && event.button !== 0)) return;
    const target = buttonRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: rect.left,
      originTop: rect.top,
      lastX: event.clientX,
      lastY: event.clientY,
      dragging: false,
    };
    movedRef.current = false;
    target.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    const dx = drag.lastX - drag.startX;
    const dy = drag.lastY - drag.startY;
    if (!drag.dragging && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      drag.dragging = true;
      movedRef.current = true;
    }
    if (!drag.dragging) return;

    applyPosition({
      left: drag.originLeft + dx,
      top: drag.originTop + dy,
    });
  };

  const finishPointer = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const target = buttonRef.current;
    const wasDragging = drag.dragging;
    dragRef.current = null;
    if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);

    if (wasDragging) {
      const position = clampPosition({
        left: drag.originLeft + (drag.lastX - drag.startX),
        top: drag.originTop + (drag.lastY - drag.startY),
      });
      applyPosition(position);
      savePosition(position);
    }
  };

  const handleClick = (event) => {
    if (!isWhotGame) {
      navigate('/support');
      return;
    }
    if (movedRef.current) {
      event.preventDefault();
      movedRef.current = false;
      return;
    }
    navigate('/support');
  };

  const sharedStyle = isWhotGame
    ? {
        position: 'fixed',
        left: String(defaultPosition().left) + 'px',
        top: String(defaultPosition().top) + 'px',
        right: 'auto',
        bottom: 'auto',
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 210,
      }
    : {
        position: 'fixed',
        right: 16,
        bottom: 'calc(16px + env(safe-area-inset-bottom))',
        zIndex: 1200,
      };

  if (!shouldRender) return null;

  return (
    <button
      ref={buttonRef}
      aria-label="Open 9jaWin Support"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPointer}
      onPointerCancel={finishPointer}
      style={{
        ...sharedStyle,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        borderRadius: BUTTON_SIZE / 2,
        border: '1px solid #2b5b50',
        background: '#00d4aa',
        color: '#06100d',
        fontSize: 22,
        fontWeight: 900,
        boxShadow: '0 8px 24px rgba(0,0,0,.35)',
        cursor: isWhotGame ? 'grab' : 'pointer',
      }}
    >
      ?
    </button>
  );
}
